"use client"

import React from "react"
import { Camera, Video, Save, Plus } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

interface FloatingActionButtonProps {
  onTakePicture: () => Promise<void>
}

export default function FloatingActionButton({
  onTakePicture,
}: FloatingActionButtonProps) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [showComingSoon, setShowComingSoon] = React.useState(false)

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const handleTakePicture = async () => {
    await onTakePicture()
    setIsExpanded(false)
  }

  const handleRecordingComingSoon = () => {
    setShowComingSoon(true)
    setTimeout(() => setShowComingSoon(false), 3000)
  }
  const {t} = useTranslation("common")
  // We don't show recording indicator as it's coming soon

  return (
    <>
      {/* Main FAB container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3">
        {/* Expanded buttons */}
        <AnimatePresence>
          {isExpanded && (
            <div className="flex flex-col items-center gap-3 mb-3">
              {/* Video recording button with Coming Soon tooltip */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.1 }}
                className="relative"
              >
                <button
                  onClick={handleRecordingComingSoon}
                  className="w-12 h-12 rounded-full bg-[#6c99561a] text-white flex items-center justify-center shadow-lg hover:bg-[#5a834700] transition-colors relative"
                >
                  <Video size={20} />
                </button>
                <AnimatePresence>
                  {showComingSoon && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      className="bg-background/60 absolute top-[-40px] left-1/2 transform -translate-x-1/2 text-white text-xs py-1 px-2 rounded whitespace-nowrap"
                    >
                      {t("common:buttons.comingSoon")}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Camera button */}
              <motion.button
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.2 }}
                onClick={handleTakePicture}
                className="w-12 h-12 rounded-full bg-[#6c9956] text-white flex items-center justify-center shadow-lg hover:bg-[#5a8347] transition-colors"
              >
                <Camera size={20} />
              </motion.button>
            </div>
          )}
        </AnimatePresence>

        {/* Main FAB button */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.1 }}
          onClick={toggleExpand}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300",
            isExpanded ? "bg-gray-700 rotate-45" : "bg-[#6c9956]",
          )}
        >
          {isExpanded ? (
            <span className="text-white text-2xl font-bold">
              <Plus size={24} />
            </span>
          ) : (
            <Save size={24} className="text-white" />
          )}
        </motion.button>
      </div>
    </>
  )
}

